import Info from '@mdi/svg/svg/information.svg?react';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import { COLOR, FONT_SIZE, SIZE } from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { Button } from '../Button';
import { FieldInfo } from '../FieldInfo';
import { MDIIcon } from '../MDIIcon';
import { PopoverTooltip, usePopoverTooltip } from '../PopoverTooltip';
import { type CheckboxProps } from './Checkbox.types';

const Checkbox: React.FC<CheckboxProps> = memo((props) => {
  // Destructure Props
  const {
    autoFocus = false,
    checkedIcon,
    errorText,
    hintText,
    iconType = 'checkmark',
    infoIconTooltip,
    labelText,
    labelElement,
    name,
    onBlur,
    title,
    uncheckedIcon,
    changeParams = EMPTY_OBJ,
    checked = false,
    className = '',
    colorMode = 'light',
    dataTestId = 'checkbox',
    disabled = false,
    labelStyle = EMPTY_OBJ,
    isSingle = false,
    onChange,
    refFunc,
    spacing,
    style = EMPTY_OBJ,
    type = 'vertical',
    changeByProp,
    withInfoIcon,
    tooltip,
  } = props;

  const popoverTooltip = usePopoverTooltip();

  // State
  const [focus, setFocus] = useState(false);
  const [hover, setHover] = useState(false);
  // Ref
  const textField = useRef<HTMLInputElement | null>(null);

  // eslint-disable-next-line
  // @ts-ignore
  const trapKeyDown = (event) => {
    // Trap ENTER and SPACE when the checkbox is in focus
    if ([13, 32].includes(event.keyCode) && focus) {
      toggleCheckbox();
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const setupReference = useCallback(
    (node: HTMLInputElement) => {
      textField.current = node;
      if (refFunc) refFunc(node);
    },
    [refFunc],
  );

  const preventExternalKeyDown = () => {
    document.addEventListener('keydown', trapKeyDown, true);
  };

  const allowExternalKeyDown = () => {
    document.removeEventListener('keydown', trapKeyDown, true);
  };

  const addFocus = () => {
    if (!focus) {
      preventExternalKeyDown();
      setFocus(true);
    }
  };

  const removeFocus = () => {
    if (focus) {
      allowExternalKeyDown();
      setFocus(false);
    }
  };

  const addHover = () => {
    if (!hover) {
      setHover(true);
    }
  };

  const removeHover = () => {
    if (hover) {
      setHover(false);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const handleChange = (event) => callOnChange(event.target.checked);

  const toggleCheckbox = () => callOnChange(!checked);

  // eslint-disable-next-line
  // @ts-ignore
  const callOnChange = (value) => {
    if (changeByProp) {
      if (!isEmpty(changeParams)) {
        onChange(changeByProp, value, ...changeParams);
      } else {
        onChange(changeByProp, value);
      }
    } else {
      onChange({ ...changeParams, value });
    }
  };

  const fieldsetClass = classNames('checkbox', `mode-${colorMode}`, className, type, spacing, {
    active: focus || hover,
    disabled,
    error: errorText,
    single: isSingle,
  });

  return (
    <CheckboxWrapper
      className={fieldsetClass}
      onFocus={addFocus}
      onBlur={removeFocus}
      onMouseOver={addHover}
      onMouseOut={removeHover}
      style={style || {}}
      data-testid={`${dataTestId}-wrapper`}
    >
      {title && (
        <CheckboxTitle data-testid="title" className="checkbox-title">
          {title}
        </CheckboxTitle>
      )}
      {title && hintText && <FieldInfo text={hintText} />}
      <CheckboxLabel htmlFor={name} onClick={removeFocus} style={labelStyle}>
        <CheckmarkWrapper className="checkmark-wrapper" ref={popoverTooltip.setTriggerRef}>
          <CheckboxInput
            autoFocus={autoFocus}
            checked={checked}
            disabled={disabled}
            id={name}
            data-testid={`${dataTestId}-status`}
            name={name}
            onBlur={onBlur}
            onKeyDown={a11yKey(toggleCheckbox)}
            onChange={handleChange}
            ref={setupReference}
            type="checkbox"
          />
          {checkedIcon ? (
            <Icon className="icon">
              {checked && checkedIcon}
              {!checked && uncheckedIcon}
            </Icon>
          ) : (
            <CheckedIcon className={iconType}></CheckedIcon>
          )}
        </CheckmarkWrapper>
        {labelText && (
          <span data-testid={`${dataTestId}-label`} className="label-text">
            {labelText}
          </span>
        )}
        {labelElement}
        {withInfoIcon && (
          <Button
            className="select-info"
            htmlButtonType="button"
            icon={<MDIIcon icon={Info} className="icon-info" />}
            type="clear"
            iconOnly
            tooltip={infoIconTooltip}
            tooltipStyle={{ maxWidth: '250px' }}
          />
        )}
      </CheckboxLabel>
      {!title && !errorText && hintText && <FieldInfo text={hintText} colorMode={colorMode} />}
      {errorText && <FieldInfo type="error" text={errorText} colorMode={colorMode} />}
      {tooltip && <PopoverTooltip {...popoverTooltip}>{tooltip}</PopoverTooltip>}
    </CheckboxWrapper>
  );
});

// Styled Components
const CheckedIcon = styled.span`
  height: ${SIZE[16]};
  left: 0;
  position: absolute;
  top: 0;
  border: 1px solid ${COLOR['fieldLabelColor']};
  border-radius: 2px;
  box-sizing: border-box;
  width: ${SIZE['16']};

  &.checkmark {
    &::after {
      -ms-transform: rotate(45deg);
      -webkit-transform: rotate(45deg);
      border: 3px solid ${COLOR['white']};
      border-top: 0;
      border-left: 0;
      box-sizing: content-box;
      content: '';
      display: none;
      height: 7px;
      left: 4px;
      position: absolute;
      top: 1px;
      transform: rotate(45deg);
      width: 3px;
    }
  }

  &.minus {
    &::after {
      background: ${COLOR['white']};
      display: none;
      content: '';
      height: 2.5px;
      position: absolute;
      width: 8px;
      top: 6px;
      left: 3px;
    }
  }
`;

const CheckboxWrapper = styled.fieldset`
  border: none;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &.mode-dark {
    label,
    .checkbox-title {
      color: ${COLOR['white']};
    }

    .checkmark-wrapper {
      input {
        &:checked:not([disabled]) ~ ${CheckedIcon} {
          background-color: ${COLOR['turquoise']};
          border-color: ${COLOR['turquoise']};
        }
      }
    }

    &:not(.disabled) {
      .checkmark-wrapper {
        ${CheckedIcon} {
          border-color: ${COLOR['white']};
        }
      }
    }

    &.active:not(.disabled) {
      .label-text {
        color: ${COLOR['turquoise-light']};
      }

      input ~ ${CheckedIcon}, input:checked ~ ${CheckedIcon} {
        background-color: ${COLOR['turquoise']};
        border-color: ${COLOR['turquoise']};
      }
    }
  }

  &.disabled {
    opacity: 0.7;

    &,
    .checkmark-wrapper,
    input:disabled,
    label {
      cursor: not-allowed;
    }

    &.checkmark-wrapper input ~ ${CheckedIcon}, input:checked ~ ${CheckedIcon} {
      background-color: ${COLOR['light-gray']};
      border-color: ${COLOR['light-gray']};
    }
  }

  &.single {
    margin-bottom: 0;

    label {
      margin-bottom: 0;
    }
  }

  &.padded {
    margin: 0 0 ${SIZE['16']} 0;
    &.horizontal {
      margin: ${SIZE['16']} ${SIZE['20']} ${SIZE['16']} 0;
    }
  }

  &.tight {
    margin: 0 0 ${SIZE['8']};
    .checkmark-wrapper {
      margin-right: ${SIZE['4']};
    }
    &.horizontal {
      margin: ${SIZE['16']} ${SIZE['20']} ${SIZE['16']} 0;
    }
  }

  &.spaced {
    .checkmark-wrapper {
      margin-right: ${SIZE['20']};
    }
  }

  &.horizontal {
    margin: 0 ${SIZE['20']} 0 0;
  }

  &.no-label {
    margin: 0;

    label,
    .checkmark-wrapper {
      margin: 0;
    }
  }

  &.error {
    label {
      color: ${COLOR['red-dark']};
    }

    &.checkmark-wrapper input ~ ${CheckedIcon}, input:checked ~ ${CheckedIcon} {
      background-color: ${COLOR['red-dark']};
      border-color: ${COLOR['red-dark']};
    }
  }

  &.active:not(.disabled) {
    label {
      color: ${COLOR['primary']};
    }

    input ~ ${CheckedIcon}, input:checked ~ ${CheckedIcon} {
      background-color: ${COLOR['primary']};
      border-color: ${COLOR['primary']};
    }
  }

  &,
  label,
  input {
    box-sizing: border-box;
  }
`;

const CheckboxTitle = styled.h3`
  font-weight: 400;
  font-size: ${FONT_SIZE['14']};
  padding: 0;
  margin: 0 0 ${SIZE['8']};
  color: ${COLOR['fieldLabelColor']};

  & + .field-legend {
    margin-top: -3px;
    margin-bottom: 5px;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  color: ${COLOR['fieldLabelColor']};
  font-size: ${FONT_SIZE['14']};
  padding: 0;
  align-items: center;
  cursor: pointer;
  justify-content: flex-start;
  margin: 0;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;

  .select-info.clear {
    margin: 0;

    .icon-info {
      fill: ${COLOR['light-gray-darkest']};
      width: ${SIZE['16']};
      height: ${SIZE['16']};
      top: 0;
      margin: 0;
    }

    &:hover .icon-info {
      fill: ${COLOR['turquoise-dark']};
    }
  }
`;

const CheckboxInput = styled.input`
  cursor: pointer;
  height: 0;
  opacity: 0;
  position: absolute;
  width: 0;

  &:checked ~ ${CheckedIcon} {
    &::after {
      display: block !important;
    }
  }

  &:checked:not([disabled]) ~ ${CheckedIcon} {
    background-color: ${COLOR['primary']};
    border-color: ${COLOR['primary']};
  }
`;

const CheckmarkWrapper = styled.span`
  cursor: pointer;
  display: inline-block;
  font-size: ${FONT_SIZE['22']};
  height: ${SIZE['16']};
  margin: 0 ${SIZE['8']} 0 0;
  position: relative;
  vertical-align: text-bottom;
  width: ${SIZE['16']};
`;

const Icon = styled.span`
  height: ${SIZE['16']};
  left: 0;
  position: absolute;
  top: 0;
  align-items: center;
  display: flex;
  font-size: ${SIZE['16']};
  justify-content: center;
`;

// Display Name
Checkbox.displayName = 'Checkbox';

export default Checkbox;
